import {
    Box,
    Flex,
    Input,
    List,
    ListItem,
    Portal,
    Spinner,
    Text,
} from '@chakra-ui/react';
import {
    ReactNode,
    RefObject,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { inventoryFieldProps } from '../../utils/inventoryFieldStyles';

export interface TypeaheadOption {
    value: string;
    label: string;
    /** Small trailing pill on the option row (e.g. "Recent"). */
    badge?: string;
}

/** Horizontal padding of inline dropdown options (matches `px={3}`), used to
 * align option text with the field text while keeping the pane's inner margin. */
const INLINE_OPTION_PAD = 12;
const MENU_MAX_H = 200;
const MENU_MIN_H = 80;
const MENU_GAP = 4;
const MENU_VIEWPORT_PAD = 8;

function computeFixedMenuPos(rect: DOMRect): {
    top?: number;
    bottom?: number;
    left: number;
    width: number;
    maxH: number;
} {
    const spaceBelow = window.innerHeight - rect.bottom - MENU_GAP - MENU_VIEWPORT_PAD;
    const spaceAbove = rect.top - MENU_GAP - MENU_VIEWPORT_PAD;
    // Prefer opening downward (overlaying footers etc. since we're portaled to
    // the viewport); only flip up when the space below is genuinely unusable.
    const openUp = spaceBelow < MENU_MIN_H && spaceAbove > spaceBelow;
    const available = Math.max(MENU_MIN_H, openUp ? spaceAbove : spaceBelow);
    const maxH = Math.min(MENU_MAX_H, available);
    let left = rect.left;
    const width = rect.width;
    if (left + width > window.innerWidth - MENU_VIEWPORT_PAD) {
        left = Math.max(MENU_VIEWPORT_PAD, window.innerWidth - width - MENU_VIEWPORT_PAD);
    }
    if (left < MENU_VIEWPORT_PAD) left = MENU_VIEWPORT_PAD;
    if (openUp) {
        return {
            bottom: window.innerHeight - rect.top + MENU_GAP,
            left,
            width,
            maxH,
        };
    }
    return {
        top: rect.bottom + MENU_GAP,
        left,
        width,
        maxH,
    };
}

export { computeFixedMenuPos };

interface InventoryTypeaheadFieldProps {
    value: string;
    onChange: (value: string) => void;
    options: TypeaheadOption[];
    onSearch?: (query: string) => void;
    /** When set, focusing with query equal to this label shows the full options list. */
    selectedLabel?: string;
    placeholder?: string;
    isLoading?: boolean;
    isDisabled?: boolean;
    size?: 'sm' | 'md';
    minSearchLength?: number;
    /** Borderless, card-anchored variant used for inline field editing. */
    inline?: boolean;
    /** When set (with `inline`), the dropdown is positioned below this element,
     * aligned to its left border and matching its width. */
    anchorRef?: RefObject<HTMLElement | null>;
    fontSize?: string;
    fontWeight?: string;
    color?: string;
    autoFocus?: boolean;
    onBlur?: (query: string) => void;
    /** When true, shows a Load more row at the bottom of results. */
    hasMore?: boolean;
    onLoadMore?: () => void;
    /**
     * Rich placeholder rendered over the empty input (native placeholders
     * cannot mix font sizes). Suppresses the plain `placeholder` text.
     */
    placeholderNode?: ReactNode;
}

const InventoryTypeaheadField = ({
    value,
    onChange,
    options,
    onSearch,
    selectedLabel,
    placeholder = 'Search…',
    isLoading = false,
    isDisabled = false,
    size = 'sm',
    minSearchLength = 0,
    inline = false,
    anchorRef,
    fontSize,
    fontWeight,
    color,
    autoFocus = false,
    onBlur,
    hasMore = false,
    onLoadMore,
    placeholderNode,
}: InventoryTypeaheadFieldProps) => {
    const [query, setQuery] = useState(value);
    const [showResults, setShowResults] = useState(false);
    const [menuPos, setMenuPos] = useState<{
        top?: number;
        bottom?: number;
        left: number;
        width: number;
        maxH: number;
    } | null>(null);
    const [inlinePos, setInlinePos] = useState<{
        top?: number;
        bottom?: number;
        left: number;
        width: number;
        maxH: number;
    } | null>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const menuRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        setQuery(value);
    }, [value]);

    useEffect(() => {
        if (onSearch && query.length >= minSearchLength) {
            onSearch(query);
        }
    }, [query, onSearch, minSearchLength]);

    const filteredOptions = useMemo(() => {
        if (onSearch) return options;
        const q = query.trim().toLowerCase();
        if (!q) return options;
        // Only skip filtering when an explicit selectedLabel is still showing — never
        // use `value`, which is often the live typed query in controlled drawers.
        const selected = (selectedLabel ?? '').trim().toLowerCase();
        // Show full list when the field still shows the currently selected label.
        if (selected && q === selected) return options;
        return options.filter(
            (opt) =>
                opt.label.toLowerCase().includes(q) ||
                opt.value.toLowerCase().includes(q),
        );
    }, [options, query, onSearch, selectedLabel]);

    const updateMenuPosition = () => {
        const el = inputRef.current;
        if (!el) return;
        setMenuPos(computeFixedMenuPos(el.getBoundingClientRect()));
    };

    useLayoutEffect(() => {
        if (inline) return;
        if (!showResults) {
            setMenuPos(null);
            return;
        }
        updateMenuPosition();
        const onScrollOrResize = () => updateMenuPosition();
        window.addEventListener('resize', onScrollOrResize);
        window.addEventListener('scroll', onScrollOrResize, true);
        return () => {
            window.removeEventListener('resize', onScrollOrResize);
            window.removeEventListener('scroll', onScrollOrResize, true);
        };
    }, [showResults, filteredOptions.length, inline]);

    useLayoutEffect(() => {
        if (!inline || !anchorRef) return;
        if (!showResults) {
            setInlinePos(null);
            return;
        }
        const measure = () => {
            const container = containerRef.current;
            const anchor = anchorRef.current;
            const input = inputRef.current;
            if (!container || !anchor || !input) return;
            const c = container.getBoundingClientRect();
            const a = anchor.getBoundingClientRect();
            const i = input.getBoundingClientRect();
            const fixed = computeFixedMenuPos(a);
            const openUp = fixed.bottom != null;
            setInlinePos({
                ...(openUp
                    ? { bottom: c.bottom - a.top + MENU_GAP }
                    : { top: a.bottom - c.top + MENU_GAP }),
                left: i.left - c.left - INLINE_OPTION_PAD,
                width: a.width,
                maxH: fixed.maxH,
            });
        };
        measure();
        window.addEventListener('resize', measure);
        window.addEventListener('scroll', measure, true);
        return () => {
            window.removeEventListener('resize', measure);
            window.removeEventListener('scroll', measure, true);
        };
    }, [inline, anchorRef, showResults, filteredOptions.length]);

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            const target = e.target as Node;
            if (containerRef.current?.contains(target)) return;
            if (menuRef.current?.contains(target)) return;
            setShowResults(false);
            inputRef.current?.blur();
        };
        document.addEventListener('mousedown', handleClickOutside, true);
        return () => document.removeEventListener('mousedown', handleClickOutside, true);
    }, []);

    const handleSelect = (opt: TypeaheadOption) => {
        onChange(opt.value);
        setQuery(opt.label);
        setShowResults(false);
        onBlur?.(opt.label);
        inputRef.current?.blur();
    };

    const handleBlur = () => {
        requestAnimationFrame(() => {
            if (
                !containerRef.current?.contains(document.activeElement) &&
                !menuRef.current?.contains(document.activeElement)
            ) {
                setShowResults(false);
                onBlur?.(query);
            }
        });
    };

    const menuMaxH = menuPos?.maxH ?? inlinePos?.maxH ?? MENU_MAX_H;

    const resultsList =
        filteredOptions.length > 0 || hasMore ? (
            <List
                bg="white"
                border="1px solid"
                borderColor="gray.200"
                borderRadius="md"
                boxShadow="none"
                maxH={`${menuMaxH}px`}
                overflowY="auto"
                onScroll={(e) => {
                    if (!hasMore || !onLoadMore || isLoading) return;
                    const el = e.currentTarget;
                    const nearBottom =
                        el.scrollTop + el.clientHeight >= el.scrollHeight - 40;
                    if (nearBottom) {
                        onLoadMore();
                    }
                }}
            >
                {filteredOptions.map((opt) => (
                    <ListItem
                        key={opt.value}
                        px={3}
                        py={2}
                        fontSize={inline ? fontSize ?? 'sm' : 'sm'}
                        color={inline ? color : undefined}
                        cursor="pointer"
                        _hover={{ bg: 'gray.50' }}
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        gap={2}
                        onMouseDown={(e) => {
                            e.preventDefault();
                            handleSelect(opt);
                        }}
                    >
                        <Text as="span" noOfLines={1}>
                            {opt.label}
                        </Text>
                        {opt.badge && (
                            <Box
                                as="span"
                                flexShrink={0}
                                fontSize="10px"
                                fontWeight="semibold"
                                letterSpacing="0.04em"
                                textTransform="uppercase"
                                px="6px"
                                py="1px"
                                borderRadius="full"
                                bg="bg.subtle"
                                color="fg.muted"
                            >
                                {opt.badge}
                            </Box>
                        )}
                    </ListItem>
                ))}
                {hasMore && isLoading && (
                    <ListItem px={3} py={2}>
                        <Text
                            fontSize={inline ? fontSize ?? 'sm' : 'sm'}
                            color="gray.500"
                        >
                            Loading…
                        </Text>
                    </ListItem>
                )}
            </List>
        ) : (
            !isLoading &&
            query.length >= minSearchLength && (
                <Box
                    bg="white"
                    border="1px solid"
                    borderColor="gray.200"
                    borderRadius="md"
                    boxShadow="none"
                    px={3}
                    py={2}
                >
                    <Text fontSize={inline ? fontSize ?? 'sm' : 'sm'} color="gray.500">
                        No results
                    </Text>
                </Box>
            )
        );

    const dropdown =
        showResults && !isDisabled ? (
            inline ? (
                anchorRef && inlinePos ? (
                    <Box
                        ref={menuRef}
                        position="absolute"
                        top={inlinePos.top != null ? `${inlinePos.top}px` : undefined}
                        bottom={inlinePos.bottom != null ? `${inlinePos.bottom}px` : undefined}
                        left={`${inlinePos.left}px`}
                        width={`${inlinePos.width}px`}
                        zIndex={2000}
                    >
                        {resultsList}
                    </Box>
                ) : anchorRef ? null : (
                    <Box
                        ref={menuRef}
                        position="absolute"
                        top="100%"
                        left={0}
                        mt={1}
                        w="100%"
                        zIndex={2000}
                    >
                        {resultsList}
                    </Box>
                )
            ) : menuPos ? (
                <Portal>
                    <Box
                        ref={menuRef}
                        position="fixed"
                        top={menuPos.top != null ? `${menuPos.top}px` : undefined}
                        bottom={menuPos.bottom != null ? `${menuPos.bottom}px` : undefined}
                        left={`${menuPos.left}px`}
                        width={`${menuPos.width}px`}
                        zIndex={2000}
                    >
                        {resultsList}
                    </Box>
                </Portal>
            ) : null
        ) : null;

    return (
        <Box ref={containerRef} position="relative">
            <Input
                ref={inputRef}
                size={size}
                variant={inline ? 'unstyled' : 'outline'}
                fontSize={fontSize}
                fontWeight={fontWeight}
                color={color}
                autoFocus={autoFocus}
                value={query}
                onChange={(e) => {
                    const next = e.target.value;
                    setQuery(next);
                    onChange(next);
                    setShowResults(true);
                }}
                onFocus={() => {
                    setShowResults(true);
                    if (onSearch && query.length >= minSearchLength) {
                        onSearch(query);
                    }
                }}
                onBlur={handleBlur}
                placeholder={placeholderNode ? undefined : placeholder}
                isDisabled={isDisabled}
                {...(inline ? {} : inventoryFieldProps)}
            />
            {placeholderNode && query === '' && (
                <Flex
                    position="absolute"
                    inset={0}
                    align="center"
                    px={3}
                    pointerEvents="none"
                    color="fg.muted"
                >
                    {placeholderNode}
                </Flex>
            )}
            {isLoading && (
                <Box position="absolute" right={3} top="50%" transform="translateY(-50%)">
                    <Spinner size="xs" />
                </Box>
            )}
            {dropdown}
        </Box>
    );
};

export default InventoryTypeaheadField;
